import Color from 'color';
import type { NodeEntry } from 'slate';
import { Text } from 'slate';

import type { RenderTextOptions } from './text';
import { renderText } from './text';
import { CHECKBOX_BORDER_THICKNESS_PX, isCheckboxCheckedText } from '../../constants';
import type { RenderModelCheckbox } from '../../slate-api';
import {
  getToggleCheckboxEdit,
  isDeleteEdit,
  isInsertCheckboxEdit,
  isInsertEdit,
  isRemoveCheckboxEdit,
  Queries,
} from '../../slate-api';
import { getEditColor } from '../../util';
import type { RenderContext } from '../engine/render-context';

const CHECKBOX_CHECK_THICKNESS = 2;
const CHECKBOX_CHECK_MARGIN = 4;

export function renderCheckbox(
  renderContext: RenderContext,
  rMCheckboxEntry: NodeEntry<RenderModelCheckbox>,
  options: RenderTextOptions
) {
  const { editor } = renderContext;
  const isPDFExport = renderContext.withContext('isPDFExport');
  const currentUserId = renderContext.withContext('currentUserId');
  const [rMCheckbox, rMCheckboxPath] = rMCheckboxEntry;
  const { children, optionId, suggestedEdits = [] } = rMCheckbox;
  const { baselinePx, inheritDeleteEdit, inheritInsertEdit, overrideColor } = options;

  const textNode = children.at(0);
  if (!textNode || !Text.isText(textNode))
    throw new Error(`Expected checkbox to have a text state child at ${rMCheckboxPath}`);

  const toggleEdit = getToggleCheckboxEdit(rMCheckbox);

  const insertEdit = toggleEdit
    ? null
    : (suggestedEdits?.find(isInsertCheckboxEdit) ??
      inheritInsertEdit ??
      textNode.suggestedEdits?.find(isInsertEdit));

  const removeEdit = toggleEdit
    ? null
    : (suggestedEdits?.find(isRemoveCheckboxEdit) ??
      inheritDeleteEdit ??
      textNode.suggestedEdits?.find(isDeleteEdit));

  const insertColor = insertEdit ? getEditColor(insertEdit, { currentUserId }) : null;
  const removeColor = removeEdit ? getEditColor(removeEdit, { currentUserId }) : null;
  const toggleColor = toggleEdit ? getEditColor(toggleEdit, { currentUserId }) : null;

  const activeTextProps = Queries.getActiveTextProps(textNode);
  const borderColor = Color(activeTextProps.fontColor.color).hex();
  const strokeColor = removeColor ?? insertColor ?? toggleColor ?? overrideColor ?? borderColor;
  const checkboxSizePx = Queries.getRMNodeWidthPx(editor, rMCheckboxEntry);
  const checkboxTopY = baselinePx - checkboxSizePx;

  const isChecked = !removeEdit && isCheckboxCheckedText(toggleEdit?.value.to ?? textNode.text);
  const isToggelable = !isPDFExport && !!currentUserId && !removeEdit;

  renderContext.renderChildren(
    rMCheckboxEntry,
    { text: renderText },
    {
      ...options,
      inlineVoid: {
        caret: 'hidden',
        overrideWidthPx: checkboxSizePx,
      },
    }
  );

  // Border
  renderContext.drawRect(0, checkboxTopY, checkboxSizePx, checkboxSizePx, {
    stroke: { color: strokeColor, thickness: CHECKBOX_BORDER_THICKNESS_PX },
  });

  // Checkmark
  if (isChecked) {
    renderContext.drawLineSegment({
      color: 'black',
      endX: checkboxSizePx / 2,
      endY: checkboxTopY + checkboxSizePx - CHECKBOX_CHECK_MARGIN,
      lineCap: 'round',
      startX: CHECKBOX_CHECK_MARGIN,
      startY: checkboxTopY + checkboxSizePx / 2,
      thickness: CHECKBOX_CHECK_THICKNESS,
    });

    renderContext.drawLineSegment({
      color: 'black',
      endX: checkboxSizePx - CHECKBOX_CHECK_MARGIN,
      endY: checkboxTopY + CHECKBOX_CHECK_MARGIN,
      lineCap: 'round',
      startX: checkboxSizePx / 2,
      startY: checkboxTopY + checkboxSizePx - CHECKBOX_CHECK_MARGIN,
      thickness: CHECKBOX_CHECK_THICKNESS,
    });
  }

  // Deletion strikethrough
  if (removeColor) {
    renderContext.drawHorizontalLine({
      color: removeColor,
      endX: checkboxSizePx,
      startX: 0,
      thickness: 2,
      y: checkboxTopY + checkboxSizePx / 2,
    });
  }

  // Insertion underline
  if (insertColor) {
    renderContext.drawHorizontalLine({
      color: insertColor,
      endX: checkboxSizePx,
      startX: 0,
      thickness: CHECKBOX_BORDER_THICKNESS_PX,
      y: checkboxTopY + checkboxSizePx,
    });
  }

  // Button
  if (isToggelable && optionId) {
    renderContext.renderReactComponent(
      0,
      checkboxTopY,
      'CheckboxButton',
      {
        clippable: true,
        key: `${rMCheckboxPath}-button`,
        zIndex: 2,
      },
      {
        height: checkboxSizePx,
        isChecked,
        isDisabled: Boolean(overrideColor),
        optionId,
        width: checkboxSizePx,
      }
    );
  }
}
